if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.setupFrameFinishVisibility();
      this.setupCustomSizeCta();
      this.setupFullscreenPrintQuality();
    }

    normalizeText(value = '') {
      return String(value).replace(/\s+/g, ' ').trim().toLowerCase();
    }

    getOptionLabelText(element) {
      if (!element) return '';
      const bits = [];
      const id = element.id;
      if (id) {
        const label = document.querySelector(`label[for="${CSS.escape(id)}"]`);
        if (label) bits.push(label.textContent || '');
      }
      const aria = element.getAttribute && element.getAttribute('aria-label');
      const name = element.getAttribute && element.getAttribute('name');
      const dataName = element.getAttribute && (element.getAttribute('data-name') || element.getAttribute('data-label') || element.getAttribute('data-title'));
      if (aria) bits.push(aria);
      if (name) bits.push(name);
      if (dataName) bits.push(dataName);
      const group = element.closest && element.closest('.gpo-element, [class*="gpo-element"], fieldset, .form__label, [class*="globo"]');
      if (group) bits.push(group.textContent || '');
      return this.normalizeText(bits.join(' '));
    }

    getFinishControl() {
      const controls = Array.from(document.querySelectorAll('select, input[type="radio"], input[type="checkbox"]'));
      return controls.find((control) => {
        const text = this.getOptionLabelText(control);
        if (text.includes('frame finish')) return false;
        return /(^|\s)finish(\s|$)/.test(text) || text.includes('print only');
      }) || null;
    }

    isPrintOnlySelected() {
      const controls = Array.from(document.querySelectorAll('select, input[type="radio"], input[type="checkbox"]'));

      const selectedControl = controls.find((control) => {
        if (control.type === 'radio' || control.type === 'checkbox') {
          if (!control.checked) return false;
        }
        const value = this.normalizeText(control.value || '');
        if (value !== 'print only' && value !== 'print-only') return false;
        const text = this.getOptionLabelText(control);
        return !text.includes('frame finish');
      });

      if (selectedControl) return true;

      return Array.from(new FormData(this.form).entries()).some(([key, value]) => {
        if (typeof value !== 'string') return false;
        const normalizedKey = this.normalizeText(key);
        const normalizedValue = this.normalizeText(value);
        return !normalizedKey.includes('frame finish') &&
          (normalizedValue === 'print only' || normalizedValue === 'print-only');
      });
    }

    getFrameFinishContainer() {
      const candidates = Array.from(document.querySelectorAll('.gpo-element, [class*="gpo-element"], fieldset, [class*="globo"]'));

      const exact = candidates.find((element) => {
        const text = this.normalizeText(element.textContent || '');
        const ownControls = Array.from(element.querySelectorAll('input, select, textarea'));
        const metadata = ownControls.map((control) => this.getOptionLabelText(control)).join(' ');
        return text.includes('frame finish') || text.includes('for framed options only') || metadata.includes('frame finish');
      });
      if (exact) return exact;

      const controls = Array.from(document.querySelectorAll('input, select, textarea'));
      const frameControl = controls.find((control) => this.getOptionLabelText(control).includes('frame finish'));
      if (!frameControl) return null;

      return frameControl.closest('.gpo-element, [class*="gpo-element"], fieldset, [class*="globo"]') || frameControl.parentElement;
    }

    getFrameFinishInputs(container) {
      if (!container) return [];
      return Array.from(container.querySelectorAll('input, select, textarea'));
    }

    updateFrameFinishVisibility() {
      const container = this.getFrameFinishContainer();
      if (!container) return;

      const hide = this.isPrintOnlySelected();
      container.style.setProperty('display', hide ? 'none' : '', hide ? 'important' : '');
      container.setAttribute('aria-hidden', hide ? 'true' : 'false');

      this.getFrameFinishInputs(container).forEach((input) => {
        input.disabled = hide;
      });
    }

    setupFrameFinishVisibility() {
      const update = () => this.updateFrameFinishVisibility();

      document.addEventListener('change', update, true);
      document.addEventListener('input', update, true);
      document.addEventListener('click', () => setTimeout(update, 0), true);

      [0, 50, 100, 250, 500, 1000, 2000, 4000].forEach((delay) => setTimeout(update, delay));

      const observer = new MutationObserver(() => update());
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'checked', 'selected', 'value', 'style'] });
    }

    setupCustomSizeCta() {
      if (this.closest('quick-add-modal')) return;

      const addCta = () => {
        const info = this.closest('.product__info-container') || document.querySelector('.product__info-container');
        if (!info || info.querySelector('.wbp-custom-size')) return;

        if (!document.getElementById('wbp-custom-size-styles')) {
          const style = document.createElement('style');
          style.id = 'wbp-custom-size-styles';
          style.textContent = `
            .wbp-custom-size {
              margin: .45rem 0 1.2rem;
              padding: .85rem 1rem;
              border-radius: .6rem;
              background: rgba(var(--color-foreground), .035);
              font-size: 1.2rem;
              line-height: 1.4;
              color: rgba(var(--color-foreground), .78);
            }
            .wbp-custom-size strong {
              color: rgb(var(--color-foreground));
              font-weight: 600;
            }
            .wbp-custom-size a {
              font-weight: 600;
              text-underline-offset: .2rem;
            }
            @media screen and (max-width: 749px) {
              .wbp-custom-size { text-align: center; }
            }
          `;
          document.head.appendChild(style);
        }

        const cta = document.createElement('div');
        cta.className = 'wbp-custom-size';
        cta.innerHTML = '<strong>Need a different size?</strong> Custom print and framed sizes may be available. <a href="/pages/contact">Ask about a custom size →</a>';

        const quantity = info.querySelector('.product-form__quantity');
        if (quantity && quantity.parentNode) {
          quantity.parentNode.insertBefore(cta, quantity);
          return;
        }

        const productForm = info.querySelector('product-form');
        if (productForm && productForm.parentNode) {
          productForm.parentNode.insertBefore(cta, productForm);
          return;
        }

        info.appendChild(cta);
      };

      addCta();
      [100, 300, 800, 1500].forEach((delay) => setTimeout(addCta, delay));
    }

    setupFullscreenPrintQuality() {
      document.querySelectorAll('.wbp-print-quality-note').forEach((note) => {
        note.style.display = 'none';
      });

      if (!document.getElementById('wbp-fullscreen-print-quality-styles')) {
        const style = document.createElement('style');
        style.id = 'wbp-fullscreen-print-quality-styles';
        style.textContent = `
          .wbp-fullscreen-print-quality {
            position: fixed;
            left: 50%;
            bottom: max(1.2rem, env(safe-area-inset-bottom));
            transform: translateX(-50%);
            z-index: 4;
            width: calc(100% - 3rem);
            max-width: 60rem;
            padding: 1rem 1.2rem;
            border-radius: .8rem;
            background: rgba(20, 20, 20, .78);
            color: #fff;
            font-size: 1.2rem;
            line-height: 1.4;
            text-align: center;
            box-shadow: 0 .3rem 1.4rem rgba(0, 0, 0, .2);
            pointer-events: none;
            -webkit-backdrop-filter: blur(4px);
            backdrop-filter: blur(4px);
          }
          .wbp-fullscreen-print-quality strong {
            color: #fff;
            font-weight: 600;
          }
          @media screen and (min-width: 750px) {
            .wbp-fullscreen-print-quality {
              bottom: 1.8rem;
              padding: 1.1rem 1.4rem;
              font-size: 1.35rem;
            }
          }
        `;
        document.head.appendChild(style);
      }

      const addNotes = () => {
        document.querySelectorAll('product-modal .product-media-modal__dialog').forEach((dialog) => {
          if (dialog.querySelector('.wbp-fullscreen-print-quality')) return;

          const note = document.createElement('div');
          note.className = 'wbp-fullscreen-print-quality';
          note.setAttribute('role', 'note');
          note.innerHTML = '<strong>Print quality:</strong> Images shown online are lower resolution and watermarked for protection. Your finished piece is produced from the <strong>high-resolution original</strong>, with <strong>no watermark</strong>.';
          dialog.appendChild(note);
        });
      };

      addNotes();
      [100, 300, 800, 1500].forEach((delay) => setTimeout(addNotes, delay));
    }

    cleanFrameFinishProperty(formData) {
      const entries = Array.from(formData.entries());
      const printOnlySelected = this.isPrintOnlySelected() || entries.some(([key, value]) => {
        if (typeof value !== 'string') return false;
        const normalizedValue = this.normalizeText(value);
        return normalizedValue === 'print only' || normalizedValue === 'print-only';
      });

      entries.forEach(([key]) => {
        if (/^properties\[\s*frame finish\s*\]$/i.test(key) && printOnlySelected) {
          formData.delete(key);
        }
      });
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      this.cleanFrameFinishProperty(formData);

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response); });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => console.error(e))
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage) this.errorMessage.textContent = errorMessage;
    }
  });
}
